import { useRef, useState } from 'react'
import { useWorkspace } from '../store/AppContext'
import type { RestoreReport } from '../lib/api'

/**
 * Backing the workspace up, and merging a backup back in.
 *
 * Owner only, because a backup holds every person's time, rates and prices in
 * one file -- a bigger disclosure than any screen makes -- and a restore
 * writes rows on other people's behalf.
 *
 * Restore ADDS; it never deletes and never overwrites. A row whose id is
 * already present is left exactly as it is, so running the same file twice
 * changes nothing the second time. The thing that costs nothing to say and a
 * lot to discover later is that this means a restore cannot undo an edit --
 * it puts back what has gone, it does not roll anything back. The panel says
 * so before the file is chosen, not after.
 */
export function BackupPanel() {
  const { ws, act, confirm, flash } = useWorkspace()
  const fileRef = useRef<HTMLInputElement>(null)

  const [busy, setBusy] = useState<'backup' | 'complete' | 'restore' | null>(null)
  const [report, setReport] = useState<RestoreReport | null>(null)

  const takeBackup = async (scope: 'standard' | 'complete') => {
    setBusy(scope === 'complete' ? 'complete' : 'backup')
    try {
      await act.backupWorkspace(scope)
    } finally {
      setBusy(null)
    }
  }

  const chooseFile = () => fileRef.current?.click()

  const onFile = async (file: File) => {
    setReport(null)

    let parsed: unknown
    try {
      parsed = JSON.parse(await file.text())
    } catch {
      flash('That file is not valid JSON.')
      return
    }

    // Named in the confirmation so the wrong file is caught before it is sent,
    // not reported back afterwards.
    const meta = parsed as { workspaceName?: unknown; takenAt?: unknown }
    const name = typeof meta.workspaceName === 'string' ? meta.workspaceName : 'an unknown workspace'
    const when =
      typeof meta.takenAt === 'string' && meta.takenAt
        ? new Date(meta.takenAt).toLocaleString()
        : 'an unknown date'

    confirm({
      title: `Restore into ${ws.name}?`,
      body:
        `This backup is from ${name}, taken ${when}. Anything in it that is missing here will be added. ` +
        `Nothing is deleted and nothing already here is changed, so an edit made after the backup stays as it is.`,
      cta: 'Restore',
      onConfirm: () => {
        void (async () => {
          setBusy('restore')
          try {
            const result = await act.restoreWorkspace(parsed)
            if (result) {
              setReport(result)
              flash(
                result.restored === 0
                  ? 'Nothing to restore — everything in that backup is already here'
                  : `Restored ${result.restored} ${result.restored === 1 ? 'row' : 'rows'}`,
              )
            }
          } finally {
            setBusy(null)
          }
        })()
      },
    })
  }

  const rows = report?.tables.filter((t) => t.found > 0) ?? []

  return (
    <div className="card card--pad" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div className="card-title">Backup &amp; restore</div>

      <div>
        <p className="pretty" style={{ fontSize: 12.5, color: 'var(--mut)', margin: '0 0 10px' }}>
          Downloads everything in {ws.name} as one file: clients, projects, tasks, tags, prices,
          charge-out rates, every time entry and every submitted week. Keep it somewhere safe — it
          contains the whole team&rsquo;s hours and rates.
        </p>
        <button
          type="button"
          className="btn"
          disabled={busy !== null}
          onClick={() => void takeBackup('standard')}
        >
          {busy === 'backup' ? 'Preparing…' : '↓ Download backup'}
        </button>

        <p
          className="pretty"
          style={{ fontSize: 12.5, color: 'var(--mut)', margin: '14px 0 10px' }}
        >
          A <strong>complete</strong> backup adds everything else this workspace owns: its own
          settings — rounding, week start, the time and duration formats, and the rules about
          requiring a project or a client — along with who was a member and at what capacity, the
          join requests, the import history, and the full audit trail of every change. Restoring one
          puts all of that back, so nothing has to be set up again by hand.
        </p>
        <p
          className="pretty"
          style={{ fontSize: 12.5, color: 'var(--mut)', margin: '0 0 10px' }}
        >
          It is the bigger disclosure of the two — it names who did what and when — so it is worth
          keeping somewhere more private than the other.
        </p>
        <button
          type="button"
          className="btn btn--sm btn--accent"
          disabled={busy !== null}
          onClick={() => void takeBackup('complete')}
        >
          {busy === 'complete' ? 'Preparing…' : '↓ Download complete backup'}
        </button>
      </div>

      <div style={{ borderTop: '1px solid var(--line2)', paddingTop: 14 }}>
        <p className="pretty" style={{ fontSize: 12.5, color: 'var(--mut)', margin: '0 0 10px' }}>
          Restoring adds back anything from the file that is missing here. It never deletes and
          never overwrites, so it is safe to run twice — but it cannot undo a change made since the
          backup, only replace what has gone.
        </p>
        <p className="pretty" style={{ fontSize: 12.5, color: 'var(--mut)', margin: '0 0 10px' }}>
          One button reads either kind: a complete file restores the settings and the record as
          well, a standard one restores the work. You do not have to say which — the file does.
        </p>
        <p className="pretty" style={{ fontSize: 12.5, color: 'var(--mut)', margin: '0 0 10px' }}>
          Logins are the one thing no file can carry. A complete backup puts people back into the
          workspace, but only where the account still exists; if the account itself is gone, they
          have to sign up again before their old time can be reattached. Time belonging to anyone
          not in the workspace is skipped and counted.
        </p>

        <input
          ref={fileRef}
          type="file"
          accept="application/json,.json"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0]
            // Cleared so choosing the same file twice fires again.
            e.target.value = ''
            if (f) void onFile(f)
          }}
        />
        <button type="button" className="btn" disabled={busy !== null} onClick={chooseFile}>
          {busy === 'restore' ? 'Restoring…' : 'Choose a backup file…'}
        </button>
      </div>

      {report && (
        <div style={{ borderTop: '1px solid var(--line2)', paddingTop: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
            {report.restored} restored
            {report.failed > 0 ? `, ${report.failed} could not be written` : ''}
          </div>

          {rows.length === 0 ? (
            <p className="pretty" style={{ fontSize: 12.5, color: 'var(--mut)', margin: 0 }}>
              That backup held nothing to add.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {rows.map((t) => (
                <div
                  key={t.table}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 10,
                    fontSize: 12.5,
                    color: 'var(--mut)',
                  }}
                >
                  <span className="mono">{t.table.replace(/_/g, ' ')}</span>
                  <span>
                    {t.restored} added
                    {t.skippedExisting > 0 ? `, ${t.skippedExisting} already here` : ''}
                    {t.skippedMissingUser > 0
                      ? `, ${t.skippedMissingUser} skipped (person gone)`
                      : ''}
                    {t.failed > 0 ? `, ${t.failed} failed` : ''}
                  </span>
                </div>
              ))}
            </div>
          )}

          {report.tables.some((t) => t.errors.length > 0) && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>
                What the database refused
              </div>
              {report.tables.flatMap((t) =>
                t.errors.map((e, i) => (
                  <div
                    key={t.table + i}
                    className="pretty"
                    style={{ fontSize: 12, color: 'var(--dg)', margin: '0 0 2px' }}
                  >
                    {t.table}: {e}
                  </div>
                )),
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
